import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { Selection } from '../App.js';
import { api } from '../lib/api.js';
import type { StudioHandoff } from '../lib/studioProject.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';
import { PublishPanel } from './PublishPanel.js';
import { SourcePanel } from './SourcePanel.js';

/**
 * Publishing as one screen instead of four.
 *
 * Choosing what to publish, choosing where, and watching it go out were three
 * destinations a person moved between while doing a single thing — and the
 * chosen post travelled between them only in the operator's memory. Here the
 * steps sit in the order they happen, and the result appears under the button
 * that caused it.
 *
 * The panels themselves are untouched. This composes them; it does not
 * reimplement them, so nothing that worked stops working.
 */
export function PublishFlow({
  selection,
  onSelect,
  onClear,
  handoff,
  onHandoffConsumed,
}: {
  selection: Selection | null;
  onSelect: (next: Selection) => void;
  onClear: () => void;
  /** Text and media the Studio is handing off — see App.tsx's `studioHandoff`. */
  handoff?: StudioHandoff | null;
  onHandoffConsumed?: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  /*
   * Once something is chosen the picker folds away, because it is a large
   * screen and the next step is the point. It reopens on request, and on its
   * own whenever there is nothing chosen.
   */
  const [pickerOpen, setPickerOpen] = useState(handoff !== null && handoff !== undefined);
  /*
   * The handoff as the composer was given it. The caller clears its copy the
   * instant it is read, so the composer is fed from here — otherwise a picker
   * that opens one render later would find the handoff already gone.
   */
  const [activeHandoff, setActiveHandoff] = useState<StudioHandoff | null>(handoff ?? null);
  const [recordFailed, setRecordFailed] = useState(false);
  const showPicker = selection === null || pickerOpen;

  /*
   * A handoff from the Studio forces the picker open on the compose tab, the
   * same way `openConversation` forces the conversations screen to a
   * particular thread — cleared the instant it is read so pressing this tab
   * again later does not replay the same attachment.
   */
  useEffect(() => {
    if (!handoff) return;
    setActiveHandoff(handoff);
    setPickerOpen(true);
    onHandoffConsumed?.();
  }, [handoff, onHandoffConsumed]);

  /*
   * The post is saved; tell the project where its output went. Failing here
   * must not look like the publish failed — the post is queued either way —
   * so it only leaves a note.
   */
  const recordPublication = (jobId: string) => {
    if (!selection?.projectId) return;
    const projectId = selection.projectId;
    setRecordFailed(false);
    api
      .recordGenerationPublication(projectId, {
        publishJobId: jobId,
        mode: 'NEW_POST',
        mediaIds: selection.projectMediaIds ?? [],
      })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['generation-project', projectId] });
        void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
      })
      .catch(() => setRecordFailed(true));
  };

  return (
    <div className="flow">
      <section className="flow-step">
        <div className="flow-head">
          <span className="flow-num">1</span>
          <h2>{t('flow.chooseContent')}</h2>
          {selection && (
            <button
              type="button"
              className="ghost small flow-toggle"
              onClick={() => setPickerOpen((open) => !open)}
            >
              {pickerOpen ? t('flow.collapse') : t('flow.change')}
            </button>
          )}
        </div>

        {selection && !pickerOpen && (
          <div className="card chosen">
            <Icon.check />
            <div className="chosen-body">
              <strong>{selection.label}</strong>
              <span className="muted small">{selection.preview}</span>
            </div>
            <button type="button" className="ghost small" onClick={onClear}>
              {t('selection.clear')}
            </button>
          </div>
        )}

        {showPicker && (
          <SourcePanel
            onSelect={(next) => {
              onSelect(next);
              setPickerOpen(false);
              setActiveHandoff(null);
            }}
            handoff={activeHandoff ?? undefined}
          />
        )}
      </section>

      <section className="flow-step">
        <div className="flow-head">
          <span className="flow-num">2</span>
          <h2>{t('flow.chooseTargets')}</h2>
        </div>
        <PublishPanel
          selection={selection}
          onPublished={recordPublication}
          onStartOver={() => {
            onClear();
            setPickerOpen(true);
          }}
        />
        {recordFailed && <p className="notice">{t('flow.projectRecordFailed')}</p>}
      </section>
    </div>
  );
}
